import { useMutation } from '@tanstack/react-query';
import { Button, Card } from 'flowbite-react';
import { CheckIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router';

import { useApi } from '../../../api/useApi.hook';
import PasswordInput from '../../../components/passwordInput/passwordInput.component';
import { isPasswordValid } from '../../../components/passwordInput/passwordRules';
import PasswordStrengthIndicator from '../../../components/passwordInput/passwordStrengthIndicator.component';
import { ProductLogo } from '../../components/productLogo/productLogo.component';
import { RouteIndex } from '../../routeIndex';

interface FormValues {
    newPassword: string;
    confirmPassword: string;
}

const ResetPasswordView = () => {
    const api = useApi();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') ?? '';

    const { register, handleSubmit, watch, formState: { errors } } = useForm<FormValues>({
        defaultValues: { newPassword: '', confirmPassword: '' }
    });

    const doReset = useMutation({
        mutationFn: (newPassword: string) =>
            api.v1.auth.provider.password.resetConfirm(token, newPassword),
    });

    if (!token) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-gray-100 p-2">
                <Card className="p-4 w-full max-w-sm shadow-lg text-center">
                    <p className='text-red-500'>Neplatný odkaz pro reset hesla.</p>
                    <Link to={RouteIndex.forgotPassword} className='text-sm text-blue-600 hover:underline'>
                        Požádat o nový odkaz
                    </Link>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex justify-center items-center min-h-screen bg-gray-100 p-2">
            <Card className="p-4 w-full max-w-sm shadow-lg">
                <ProductLogo className='text-center' />
                <div className='w-full border-t border-t-gray-200' />
                <h4 className="text-xl font-semibold text-center">Nové heslo</h4>

                {doReset.isSuccess ? (
                    <div className='flex flex-col items-center gap-3'>
                        <CheckIcon size={48} className='text-lime-700' />
                        <p className='text-center text-sm'>Heslo bylo úspěšně změněno.</p>
                        <Link to={RouteIndex.login} className='text-sm text-blue-600 hover:underline'>
                            Přihlásit se
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit((d) => doReset.mutate(d.newPassword))} className='flex flex-col gap-4'>
                        <div>
                            <PasswordInput
                                id="newPassword"
                                placeholder="Nové heslo"
                                {...register('newPassword', {
                                    required: 'Heslo je povinné',
                                    validate: v => isPasswordValid(v) || 'Heslo nesplňuje požadavky',
                                })}
                                color={errors.newPassword ? 'failure' : ''}
                            />
                            {errors.newPassword && (
                                <p className="text-sm text-red-500 mt-1">{errors.newPassword.message}</p>
                            )}
                        </div>
                        <div>
                            <PasswordInput
                                id="confirmPassword"
                                placeholder="Zopakujte heslo"
                                {...register('confirmPassword', {
                                    required: 'Potvrzení hesla je povinné',
                                    validate: (v) => v === watch('newPassword') || 'Hesla se neshodují'
                                })}
                                color={errors.confirmPassword ? 'failure' : ''}
                            />
                            {errors.confirmPassword && (
                                <p className="text-sm text-red-500 mt-1">{errors.confirmPassword.message}</p>
                            )}
                        </div>

                        {doReset.isError && (
                            <p className='text-sm text-red-500 text-center'>
                                Odkaz je neplatný nebo vypršel. <Link to={RouteIndex.forgotPassword} className='underline'>Požádejte o nový.</Link>
                            </p>
                        )}

                        <PasswordStrengthIndicator password={watch('newPassword')} />
                        <Button type="submit" color="dark" className="w-full" disabled={doReset.isPending || !isPasswordValid(watch('newPassword'))}>
                            {doReset.isPending ? 'Ukládám...' : 'Nastavit heslo'}
                        </Button>
                    </form>
                )}
            </Card>
        </div>
    );
};

export default ResetPasswordView;
